import type { RepresentativeSearchModel } from '../types/representative.type.ts';
import type { StaffMemberListModel } from '../types/staff-member.type.ts';

const escapeCsvField = (field: string | undefined | null): string => {
  if (!field) {
    return '';
  }

  const stringField = String(field);

  if (
    stringField.includes('"') ||
    stringField.includes(',') ||
    stringField.includes('\n') ||
    stringField.includes('\r')
  ) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }

  return stringField;
};

export const exportStaffToCsv = (staff: StaffMemberListModel[], accountName: string): void => {
  const sortedStaff = [...staff].sort((a, b) => {
    const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
    if (lastNameCompare !== 0) {
      return lastNameCompare;
    }
    return (a.firstName || '').localeCompare(b.firstName || '');
  });

  const headers = [
    'Account Name',
    'First Name',
    'Last Name',
    'Title',
    'Dietary Preferences',
    'Dietary Restrictions',
    'Public Notes',
  ];

  const rows = sortedStaff.map(member => {
    const preferences = [...(member.dietaryPreferences || []), member.otherDietaryPreference]
      .filter(x => x)
      .join(', ');

    const restrictions = [...(member.dietaryRestrictions || []), member.otherDietaryRestriction]
      .filter(x => x)
      .join(', ');

    return [
      escapeCsvField(accountName),
      escapeCsvField(member.firstName),
      escapeCsvField(member.lastName),
      escapeCsvField(member.title),
      escapeCsvField(preferences),
      escapeCsvField(restrictions),
      escapeCsvField(member.foodNotes),
    ];
  });

  const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');

  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const date = new Date().toISOString().split('T')[0];
  const sanitizedAccountName = accountName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
  const filename = `${sanitizedAccountName}-staff-${date}.csv`;

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportRepresentativesToCsv = (
  representatives: RepresentativeSearchModel[],
  fileLabel = 'representatives',
): void => {
  const dedupedRepresentatives = Array.from(
    new Map(
      representatives
        .map(item => item.representative)
        .filter(rep => !!rep)
        .map(rep => [rep.key, rep]),
    ).values(),
  );

  const sortedRepresentatives = [...dedupedRepresentatives].sort((a, b) => {
    const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
    if (lastNameCompare !== 0) {
      return lastNameCompare;
    }
    return (a.firstName || '').localeCompare(b.firstName || '');
  });

  const headers = [
    'Representative ID',
    'Title',
    'First Name',
    'Last Name',
    'Email',
    'Company',
    'Cell Phone',
    'Office Phone',
    'Extension',
    'Manager Name',
    'Manager Phone',
    'Manager Email',
    'Subscription Status',
    'Active',
    'Receive Chats',
    'Can Delete',
    'Created At',
    'Last Logged In At',
    'Drugs',
    'Specialties',
  ];

  const rows = sortedRepresentatives.map(rep => {
    return [
      escapeCsvField(rep.id?.toString()),
      escapeCsvField(rep.title),
      escapeCsvField(rep.firstName),
      escapeCsvField(rep.lastName),
      escapeCsvField(rep.emailAddress),
      escapeCsvField(rep.company),
      escapeCsvField(rep.cellPhone),
      escapeCsvField(rep.officePhone),
      escapeCsvField(rep.extension),
      escapeCsvField(rep.managerName),
      escapeCsvField(rep.managerPhone),
      escapeCsvField(rep.managerEmail),
      escapeCsvField(rep.subscriptionStatus),
      escapeCsvField(rep.active ? 'Yes' : 'No'),
      escapeCsvField(rep.receiveChats ? 'Yes' : 'No'),
      escapeCsvField(rep.canDelete ? 'Yes' : 'No'),
      escapeCsvField(rep.createdAt),
      escapeCsvField(rep.lastLoggedInAt),
      escapeCsvField(rep.drugs?.join(', ')),
      escapeCsvField(rep.specialties?.join(', ')),
    ];
  });

  const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');

  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const date = new Date().toISOString().split('T')[0];
  const sanitizedFileLabel = fileLabel.replace(/[^a-z0-9]/gi, '-').toLowerCase();
  const filename = `${sanitizedFileLabel}-${date}.csv`;

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
